import React, { useState, useMemo } from 'react'
import SearchBar from '../components/SearchBar'
import ContributorCard from '../components/ContributorCard'
import SEOHead, { seoConfigs } from '../components/SEOHead'
import aiContributorsData from '../data/aiContributors.json'

const ContributorsPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRole, setSelectedRole] = useState('All')

  // Get unique roles
  const roles = useMemo(() => {
    const uniqueRoles = [
      ...new Set(
        aiContributorsData.map((contributor) => {
          // Extract role type (e.g., "Professor", "CEO", etc.)
          const role = contributor.role.split(' ')[0]
          return role
        })
      ),
    ]
    return ['All', ...uniqueRoles.sort()]
  }, [])

  // Filter contributors based on search term and role
  const filteredContributors = useMemo(() => {
    return aiContributorsData.filter((contributor) => {
      const matchesSearch =
        searchTerm === '' ||
        contributor.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contributor.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contributor.affiliation
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        contributor.expertise.some((area) =>
          area.toLowerCase().includes(searchTerm.toLowerCase())
        )

      const matchesRole =
        selectedRole === 'All' ||
        contributor.role.toLowerCase().includes(selectedRole.toLowerCase())

      return matchesSearch && matchesRole
    })
  }, [searchTerm, selectedRole])

  return (
    <>
      <SEOHead {...seoConfigs.contributors} />
      <div className='space-y-8'>
        {/* Header */}
        <div className='text-center space-y-4'>
          <h1 className='text-4xl md:text-6xl font-bold text-white mb-4'>
            AI Agents
          </h1>
          <p className='text-xl text-white/80 max-w-3xl mx-auto'>
            Building a new class of enterprise-ready B2B agents—powered by
            today’s most advanced AI toolkits like OpenAI, LangChain, Replit,
            Zapier, and Windsurf. These agents are designed to solve real pain
            points across sales, marketing, and RevOps—from automating outreach
            and content workflows to extracting insights from the dark funnel.
            Whether you're running demand gen, managing CRM follow-ups, or
            scaling personalized campaigns, these agents are your unfair
            advantage in the modern go-to-market stack.
          </p>
          <div className='flex items-center justify-center space-x-4 text-white/60'>
            <div className='flex items-center space-x-2'>
              <div className='w-2 h-2 bg-emerald-500 rounded-full animate-pulse'></div>
              <span>Top {aiContributorsData.length} Agents</span>
            </div>
            <div className='flex items-center space-x-2'>
              <div className='w-2 h-2 bg-cyan-500 rounded-full animate-pulse'></div>
              <span>{roles.length - 1} AI tools</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder='Search contributors by name, role, affiliation, or expertise...'
        />

        {/* Role Filter */}
        <div className='glass-card rounded-2xl p-6'>
          <h3 className='text-white font-semibold mb-4'>
            Filter by AI Toolkit
          </h3>
          <div className='flex flex-wrap gap-2'>
            {roles.map((role) => (
              <button
                key={role}
                onClick={() => setSelectedRole(role)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  selectedRole === role
                    ? 'bg-emerald-500 text-white shadow-lg'
                    : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                }`}
              >
                {role}
                {role !== 'All' && (
                  <span className='ml-2 text-xs opacity-75'>
                    (
                    {
                      aiContributorsData.filter((contributor) =>
                        contributor.role
                          .toLowerCase()
                          .includes(role.toLowerCase())
                      ).length
                    }
                    )
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        {searchTerm && (
          <div className='text-center'>
            <p className='text-white/80'>
              Found {filteredContributors.length} agent
              {filteredContributors.length !== 1 ? 's' : ''}
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
          </div>
        )}

        {/* Contributors Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {filteredContributors.map((contributor, index) => (
            <div
              key={contributor.id}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <ContributorCard contributor={contributor} />
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredContributors.length === 0 && (
          <div className='text-center py-12'>
            <div className='glass-card rounded-2xl p-8 max-w-md mx-auto'>
              <div className='w-16 h-16 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full flex items-center justify-center mx-auto mb-4'>
                <svg
                  className='w-8 h-8 text-white'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-white mb-2'>
                No contributors found
              </h3>
              <p className='text-white/70 mb-4'>
                Try adjusting your search terms or role filter.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedRole('All')
                }}
                className='px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors'
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Call to Action
        {filteredContributors.length > 0 && (
          <div className="text-center py-8">
            <div className="glass-card rounded-2xl p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-4">
                Join the Community
              </h3>
              <p className="text-white/80 mb-6">
                Are you an AI researcher, educator, or industry leader? Join our community of contributors and share your expertise with the world.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
                  Submit Your Profile
                </button>
                <button className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-300 border border-white/20">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        )}  */}
      </div>
    </>
  )
}

export default ContributorsPage
